import * as React from 'react';
import type { TabKey } from '../TabList/Tab';
import { Tab } from '../TabList/Tab';
import { TabList } from '../TabList/TabList';
import { MessageBroker } from '~/Features/Messaging/MessageBroker';
import { useMessageHandler } from '~/Features/Messaging/useMessageHandler';
import { buildTabPanelId } from '~/Partials/Tabs/tabUtils';
import { TabSwitchRequested } from '~/Partials/TabsBar/Events/TabSwitchRequested';
import { TabSwitched } from '~/Partials/TabsBar/Events/TabSwitched';
import { usePrevious } from '~/Partials/Utils/usePrevious';
import { useHistoryState } from '~/Utils/Hooks/useHistoryState';
import styles from './TabsBar.module.css';
import type { TabsBarViewModel } from './TabsBarViewModel.csharp';

const toggleTabContent = (tabKey: TabKey, display: boolean) => {
    const tabContentId = buildTabPanelId(tabKey);
    const tabContentRef = document.getElementById(tabContentId);

    if (tabContentRef) {
        tabContentRef.style.display = display ? 'block' : 'none';
    }
};

export const TabsBar: React.FC<TabsBarViewModel> = props => {
    const { name, tabs } = props;

    const defaultTab = tabs[0].key;
    const [selectedTab, setSelectedTab] = React.useState<TabKey>(defaultTab);

    useHistoryState(`TabsBar-${name}`, selectedTab, setSelectedTab);

    React.useEffect(() => {
        tabs.forEach(tab => {
            if (defaultTab !== tab.key) {
                toggleTabContent(tab.key, false);
            }
        });
    }, []);

    const previousSelectedTabName = usePrevious(selectedTab);

    React.useEffect(() => {
        if (previousSelectedTabName) {
            toggleTabContent(previousSelectedTabName, false);
        }

        toggleTabContent(selectedTab, true);
    }, [selectedTab]);

    const switchTabs = (tabKey: TabKey) => {
        setSelectedTab(tabKey);
        MessageBroker.publish(new TabSwitched(tabKey));
    };

    useMessageHandler(TabSwitchRequested.id, (event: TabSwitchRequested) => {
        switchTabs(event.tabKey);
    });

    if (!tabs.length) {
        return null;
    }

    return (
        <TabList id="TabsBar" className={styles.TabsBar} name={name}>
            {tabs.map(tab => (
                <Tab
                    key={tab.key}
                    tabKey={tab.key}
                    selected={selectedTab === tab.key}
                    onSelect={switchTabs}
                    className={styles.Tab}
                >
                    {tab.displayName}
                </Tab>
            ))}
        </TabList>
    );
};
